import React, { Component } from 'react';
import { I18n } from 'react-redux-i18n';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import StockEntryRow from './StockEntryRow';
import { incomingStockService } from '../services/incomingStockService';
import './StockEntryTable.css';
import './ValidationErrors.css';

export const getEffectiveActivePriceField = (entries = []) => {
    const hasSupplierCode = (entries || []).some(entry => {
        const supplierCode = entry && (entry.code || entry.supplier_code);
        return !!supplierCode && String(supplierCode).trim() !== '';
    });

    if (hasSupplierCode) {
        return 'net_price';
    }

    return localStorage.input_price || 'net_unit_price';
};

class StockEntryTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            headerDefaults: {
                store: null,
                vatCategory: null,
                quantityType: null
            },
            totalNetPrice: 0,
            totalBruttoPrice: 0,
            activePriceField: getEffectiveActivePriceField(props.entries || []),
            validationErrors: []
        };
    }

    componentDidMount() {
        // Calculate totals for any entries that may already be loaded
        this.calculateTotals();
        // Calculate original_quantity for all existing entries on initial load
        this.calculateOriginalQuantitiesForAllEntries();
        // Calculate validation errors for all entries
        this.updateValidationErrors();
        this.syncActivePriceField();
        // Check for localStorage changes periodically
        this.priceFieldInterval = setInterval(() => {
            const currentPriceField = getEffectiveActivePriceField(this.props.entries || []);
            if (currentPriceField !== this.state.activePriceField) {
                this.setState({ activePriceField: currentPriceField });
            }
        }, 100);
    }

    componentWillUnmount() {
        if (this.priceFieldInterval) {
            clearInterval(this.priceFieldInterval);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Recalculate totals when entries change or when entries are first loaded
        if (prevProps.entries !== this.props.entries || 
            (prevProps.entries.length === 0 && this.props.entries.length > 0) ||
            prevProps.entries.length !== this.props.entries.length) {
            this.calculateTotals();
        }
        // Recalculate validation errors if props or state changed
        if (prevProps.entries !== this.props.entries || prevState !== this.state) {
            this.updateValidationErrors();
        }

        this.syncActivePriceField();
    }

    syncActivePriceField = () => {
        const effectivePriceField = getEffectiveActivePriceField(this.props.entries || []);
        if (effectivePriceField !== this.state.activePriceField) {
            this.setState({ activePriceField: effectivePriceField });
        }
    }
    updateValidationErrors = () => {
        const { entries } = this.props;
        const validationErrors = entries.map(entry => this.validateEntry(entry));
        this.setState({ validationErrors });
    }

    calculateTotals = () => {
        const { entries } = this.props;
        
        let totalNetPrice = 0;
        let totalBruttoPrice = 0;
        
        entries.forEach(entry => {
            totalNetPrice += incomingStockService.parseNumber(entry.net_price) || 0;
            totalBruttoPrice += incomingStockService.parseNumber(entry.brutto_price) || 0;
        });
        
        this.setState({ totalNetPrice, totalBruttoPrice });
    }

    componentDidMount() {
        // Calculate original_quantity for all existing entries on initial load
        this.calculateOriginalQuantitiesForAllEntries();
    }

    componentDidUpdate(prevProps) {
        // Recalculate if entries changed (e.g., loaded from server)
        if (prevProps.entries !== this.props.entries) {
            this.calculateOriginalQuantitiesForAllEntries();
        }
    }

    calculateOriginalQuantitiesForAllEntries = () => {
        const { entries, onChange } = this.props;
        
        let hasChanges = false;
        const updatedEntries = entries.map(entry => {
            if (entry.count && !entry.original_quantity) {
                const count = parseFloat(entry.count);
                if (!isNaN(count)) {
                    const multiplicator = entry.multiplicator || 1;
                    let originalQuantity;
                    if (multiplicator !== 1) {
                        originalQuantity = Math.round(count / multiplicator * 1000) / 1000;
                    } else {
                        originalQuantity = Math.round(count * 1000) / 1000;
                    }
                    
                    hasChanges = true;
                    return { ...entry, original_quantity: originalQuantity };
                }
            }
            return entry;
        });
        
        if (hasChanges) {
            onChange(updatedEntries);
        }
    }

    handleHeaderDefaultChange = (field, value) => {
        this.setState(prevState => ({
            headerDefaults: {
                ...prevState.headerDefaults,
                [field]: value
            }
        }));

        // Auto-add new entry if all header defaults are set and no entries exist
        const { headerDefaults } = this.state;
        const { entries } = this.props;
        
        if (entries.length === 0) {
            const newDefaults = { ...headerDefaults, [field]: value };
            if (newDefaults.store && newDefaults.vatCategory && newDefaults.quantityType) {
                this.addNewEntry();
            }
        }
    }

    addNewEntry = () => {
        const { entries, vatCategories = [] } = this.props;
        const { headerDefaults } = this.state;
        
        // Find the full VAT category object to include percentage info
        let vatCategory = null;
        if (headerDefaults.vatCategory) {
            const fullVatCategory = vatCategories.find(vat => vat.id === headerDefaults.vatCategory);
            if (fullVatCategory) {
                vatCategory = {
                    id: fullVatCategory.originalId, // Use originalId for the entry
                    percent: fullVatCategory.percent
                };
            }
        }
        
        const newEntry = {
            id: `temp-${Date.now()}`,
            code: '',
            original_quantity: '',
            count: '',
            multiplicator: 1,
            menuItem: null,
            store: headerDefaults.store ? { id: headerDefaults.store } : null,
            vatCategory: vatCategory,
            quantityType: headerDefaults.quantityType || null,
            net_unit_price: '',
            brutto_unit_price: '',
            net_price: '',
            brutto_price: '',
            expiry_date: null
        };
        
        this.props.onChange([...entries, newEntry]);
        
        // Recalculate totals immediately after adding new entry
        setTimeout(() => this.calculateTotals(), 0);
    }

    validateEntry = (entry) => {
        const errors = [];
        
        // Check if there's a supplier code but no product selected
        // If supplier code exists but no product is selected, skip all validations
        // This is a normal state during import/processing workflows, not an error
        if (entry.code && entry.code.trim() !== '' && (!entry.menuItem || !entry.menuItem.id)) {
            // Return no errors - this is a valid state during import processing
            return errors;
        }
        
        if (!entry.menuItem || !entry.menuItem.id) {
            errors.push('Product is required');
        }

        if (entry.count !== undefined && parseFloat(entry.count) === 0) {
            errors.push('Quantity must not be 0');
        }
        
        if (!entry.store || !entry.store.id) {
            errors.push('Store is required');
        }
        
        if (!entry.vatCategory || !entry.vatCategory.id) {
            errors.push('VAT category is required');
        }
        
        if (!entry.quantityType) {
            errors.push('Quantity type is required');
        }
        
        if (!entry.net_unit_price && !entry.brutto_unit_price && !entry.net_price && !entry.brutto_price) {
            errors.push('At least one price field is required');
        }
        
        return errors;
    }

    handleEntryChange = (index, updatedEntry) => {
        const { entries } = this.props;
        const newEntries = [...entries];
        
        newEntries[index] = updatedEntry;
        
        this.props.onChange(newEntries);
        
        // Recalculate totals immediately after entry change
        setTimeout(() => this.calculateTotals(), 0);
        const validationErrors = {...this.state.validationErrors};
        const validationError = this.validateEntry(updatedEntry);
        if (JSON.stringify(validationErrors[index]) !== JSON.stringify(validationError))  {
            validationErrors[index] = validationError;
            this.setState({ validationErrors });
        }
    }

    handleDeleteEntry = (index) => {
        const { entries } = this.props;
        const newEntries = entries.filter((_, i) => i !== index);
        this.props.onChange(newEntries);
        
        // Recalculate totals immediately after entry deletion
        setTimeout(() => this.calculateTotals(), 0);
    }

    // Price input mode setting
    setInputPrice = (priceType) => {
        const hasSupplierCode = (this.props.entries || []).some(entry => {
            const supplierCode = entry && (entry.code || entry.supplier_code);
            return !!supplierCode && String(supplierCode).trim() !== '';
        });
        const activePriceType = hasSupplierCode ? 'net_price' : priceType;

        localStorage.input_price = activePriceType;
        
        // Immediately update state to reflect the change in the arrow indicator
        this.setState({ activePriceField: activePriceType });
        
        // Disable all price inputs
        const priceInputs = document.querySelectorAll('input.input_price:not(.quantity)');
        priceInputs.forEach(input => input.disabled = true);
        
        // Enable only the selected price type
        const selectedInputs = document.querySelectorAll(`input.input_price.${activePriceType}`);
        selectedInputs.forEach(input => input.disabled = false);
    }

    // Renderers for static content
    renderStaticRow = () => (
        <tr className="static-row">
            <td style={{ width: '1.5rem' }}></td>
            {this.props.online_szamla !== false && <td className="supplier-code"></td>}
            {this.props.online_szamla !== false && <td className="original_quantity"></td>}
            <td className="product"></td>
            <td className="quantity-type"></td>
            <td className="vat-category"></td>
            <td className="quantity"></td>
            <td className="input_price" onClick={() => this.setInputPrice('net_unit_price')}></td>
            <td className="input_price" onClick={() => this.setInputPrice('brutto_unit_price')}></td>
            <td className="input_price" onClick={() => this.setInputPrice('net_price')}></td>
            <td className="input_price" onClick={() => this.setInputPrice('brutto_price')}></td>
            <td className="expiry-date"></td>
            <td className="store"></td>
            <td className="deleteentry"></td>
        </tr>
    )

    render() {
        const { 
            entries,
            suppliers,
            stores, 
            vatCategories, 
            quantityTypes, 
            products
        } = this.props;
        const { 
            headerDefaults,
            totalNetPrice,
            totalBruttoPrice
        } = this.state;

        // Ensure totals are calculated if entries exist but totals are 0
        if (entries && entries.length > 0 && totalNetPrice === 0 && totalBruttoPrice === 0) {
            // Check if any entry actually has price values
            const hasActualPrices = entries.some(entry => 
                (entry.net_price && parseFloat(entry.net_price) !== 0) || 
                (entry.brutto_price && parseFloat(entry.brutto_price) !== 0)
            );
            if (hasActualPrices) {
                setTimeout(() => this.calculateTotals(), 0);
            }
        }


        return (
            <div className="stock-entry-table" style={{ height: '100%', overflow: 'hidden' }}>
                <div id="entriesFreezeTable" style={{ height: '100%', overflow: 'auto' }}>
                    <table className="selectable" id="entriesTable" style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '2.1rem', textAlign: 'center' }}>
                                    <i className="pi pi-info-circle" style={{ color: '#6c757d' }} title="Entry Status"></i>
                                </th>
                                {this.props.online_szamla !== false && (
                                    <th className="supplier-code">
                                        <span className="browser-only">{I18n.t('admin_local.supplier_code')}</span>
                                        <span className="mobile-only">{I18n.t('admin_local._supplier_code')}</span>
                                    </th>
                                )}
                                {this.props.online_szamla !== false && (
                                    <th className="original_quantity">
                                        <span className="browser-only">{I18n.t('admin_local.invoice_count')}</span>
                                    </th>
                                )}
                                <th className="product">{I18n.t('admin_local.product')}</th>
                                <th className="quantity-type" style={{ minWidth: '7em' }}>
                                    {I18n.t('admin_local.quantity_type')}
                                </th>
                                <th className="vat-category">
                                    <span className="browser-only">{I18n.t('admin_local.vat_category')}</span>
                                    <span className="mobile-only">{I18n.t('admin_local._vat_category')}</span>
                                </th>
                                <th className="quantity">{I18n.t('admin_local.quantity')}</th>
                                <th className="input_price" onClick={() => this.setInputPrice('net_unit_price')}>
                                    <span className="browser-only">{I18n.t('admin_local.net_unit_price')}</span>
                                    <span className="mobile-only">{I18n.t('admin_local._net_unit_price')}</span>
                                </th>
                                <th className="input_price" onClick={() => this.setInputPrice('brutto_unit_price')}>
                                    <span className="browser-only">{I18n.t('admin_local.brutto_unit_price')}</span>
                                    <span className="mobile-only">{I18n.t('admin_local._brutto_unit_price')}</span>
                                </th>
                                <th className="input_price" onClick={() => this.setInputPrice('net_price')}>
                                    <span className="browser-only">{I18n.t('admin_local.net_price')}</span>
                                    <span className="mobile-only">{I18n.t('admin_local._net_price')}</span>
                                </th>
                                <th className="input_price" onClick={() => this.setInputPrice('brutto_price')}>
                                    <span className="browser-only">{I18n.t('admin_local.brutto_price')}</span>
                                    <span className="mobile-only">{I18n.t('admin_local._brutto_price')}</span>
                                </th>
                                <th className="expiry-date">{I18n.t('admin_local.expiry_date')}</th>
                                <th className="store">{I18n.t('admin_local.store')}</th>
                                <th className="approved_cost" >
                                    <span className="browser-only">{I18n.t('admin_local.approved_cost')}</span>
                                    <span className="mobile-only">{I18n.t('admin_local._approved_cost')}</span>
                                </th>
                                <th className="cost_change" >
                                    <span className="browser-only">{I18n.t('admin_local.cost_change')}</span>
                                    <span className="mobile-only">{I18n.t('admin_local._cost_change')}</span>
                                </th>
                                <th className="new_average_cost" >
                                    <span className="browser-only">{I18n.t('admin_local.new_average_cost')}</span>
                                    <span className="mobile-only">{I18n.t('admin_local._new_average_cost')}</span>
                                </th>
                                <th className="deleteentry"></th>
                            </tr>
                            
                            {/* Header row with defaults - matching original structure */}
                            <tr className="head hide-print">
                                <th style={{ width: '1.5rem' }}>
                                    {/* Warning Icon column - Empty */}
                                </th>
                                {this.props.online_szamla !== false && (
                                    <th style={{ minWidth: '10rem' }}>
                                        {/* Supplier Code - Empty */}
                                    </th>
                                )}
                                {this.props.online_szamla !== false && (
                                    <th className="original_quantity" style={{ minWidth: '10rem' }}>
                                        {/* Original Quantity - Empty */}
                                    </th>
                                )}
                                <th style={{ minWidth: '10rem' }}>
                                    {/* Product - Empty */}
                                </th>
                                <th className="quantity-type" style={{ minWidth: '7em' }}>
                                    {/* Quantity Type - Empty */}
                                </th>
                                <th className="vat-category">
                                    {/* VAT Category Dropdown */}
                                    <Dropdown
                                        value={headerDefaults.vatCategory}
                                        options={vatCategories}
                                        optionLabel="name"
                                        optionValue="id"
                                        placeholder={I18n.t("admin_local.select_vat_category")}
                                        onChange={(e) => this.handleHeaderDefaultChange('vatCategory', e.value)}
                                        showClear
                                        style={{ width: '100%' }}
                                        className="select2"
                                    />
                                </th>
                                <th>
                                    {/* Quantity - Empty */}
                                </th>
                                <th className="input_price">
                                    {/* Net Unit Price */}
                                    {this.state.activePriceField === 'net_unit_price' && (
                                        <div style={{ textAlign: 'center' }}>
                                            <i className="pi pi-arrow-down" style={{ color: '#6c757d', fontSize: '1rem' }} title="Active price field"></i>
                                        </div>
                                    )}
                                </th>
                                <th className="input_price">
                                    {/* Brutto Unit Price */}
                                    {this.state.activePriceField === 'brutto_unit_price' && (
                                        <div style={{ textAlign: 'center' }}>
                                            <i className="pi pi-arrow-down" style={{ color: '#6c757d', fontSize: '1rem' }} title="Active price field"></i>
                                        </div>
                                    )}
                                </th>
                                <th className="input_price">
                                    {/* Net Price */}
                                    {this.state.activePriceField === 'net_price' && (
                                        <div style={{ textAlign: 'center' }}>
                                            <i className="pi pi-arrow-down" style={{ color: '#6c757d', fontSize: '1rem' }} title="Active price field"></i>
                                        </div>
                                    )}
                                </th>
                                <th className="input_price">
                                    {/* Brutto Price */}
                                    {this.state.activePriceField === 'brutto_price' && (
                                        <div style={{ textAlign: 'center' }}>
                                            <i className="pi pi-arrow-down" style={{ color: '#6c757d', fontSize: '1rem' }} title="Active price field"></i>
                                        </div>
                                    )}
                                </th>
                                <th className="expiry-date">
                                    {/* Expiry Date - Empty */}
                                </th>
                                <th className="store">
                                    {/* Store Dropdown */}
                                    <Dropdown
                                        value={headerDefaults.store}
                                        options={stores}
                                        optionLabel="name"
                                        optionValue="id"
                                        placeholder={I18n.t("admin_local.select_store")}
                                        onChange={(e) => this.handleHeaderDefaultChange('store', e.value)}
                                        showClear
                                        style={{ width: '100%' }}
                                        className="select2"
                                    />
                                </th>
                                <th>
                                </th>
                                <th>
                                </th>
                                <th>
                                </th>
                                <th className="deleteentry">
                                    {/* Delete column header - Empty */}
                                </th>
                            </tr>
                        </thead>
                        
                        <tbody id="entriesTable">
                            {this.renderStaticRow()}
                            
                            {entries.map((entry, index) => (
                                <StockEntryRow
                                    key={entry.id || index}
                                    entry={entry}
                                    index={index}
                                    suppliers={suppliers}
                                    stores={stores}
                                    vatCategories={vatCategories}
                                    quantityTypes={quantityTypes}
                                    products={products}
                                    validationErrors={this.state.validationErrors[index]}
                                    onChange={this.handleEntryChange}
                                    onDelete={this.handleDeleteEntry}
                                    online_szamla={this.props.online_szamla}
                                    flatProducts={this.props.flatProducts}
                                />
                            ))}
                        </tbody>
                        
                        <tfoot>
                            <tr>
                                <td style={{ width: '1.5rem' }}>
                                    {/* Warning Icon column - Empty */}
                                </td>
                                {this.props.online_szamla !== false && (
                                    <td className="supplier-code">
                                        <div style={{
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '0.9em',
                                            padding: '5px'
                                        }}>
                                            {entries.length} items
                                        </div>
                                    </td>
                                )}
                                {this.props.online_szamla !== false && (
                                    <td className="original_quantity">
                                        <div style={{
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '0.9em',
                                            padding: '5px'
                                        }}>
                                            Total: {entries.reduce((sum, entry) => sum + (parseFloat(entry.original_quantity) || 0), 0).toFixed(2)}
                                        </div>
                                    </td>
                                )}
                                <td colSpan="6" className="browser-only">
                                    <div style={{ 
                                        border: 'none', 
                                        display: 'flex', 
                                        justifyContent: 'center' 
                                    }}>
                                        Total
                                    </div>
                                </td>
                                <td style={{ flexBasis: '40%' }}>
                                    <span className="mobile-only">Net Price</span>
                                    <InputText
                                        value={incomingStockService.formatNumber(totalNetPrice)}
                                        disabled
                                        className="input input_price input-numeral total_net_price"
                                        style={{ textAlign: 'right' }}
                                    />
                                </td>
                                <td style={{ flexBasis: '40%' }}>
                                    <span className="mobile-only">Brutto Price</span>
                                    <InputText
                                        value={incomingStockService.formatNumber(totalBruttoPrice)}
                                        disabled
                                        className="input input_price input-numeral total_brutto_price"
                                        style={{ textAlign: 'right' }}
                                    />
                                </td>
                                <td className="expiry-date">
                                    {/* Expiry Date - Hidden but maintains structure */}
                                </td>
                                <td colSpan="5" className="addEntry-right hide-print">
                                    <Button
                                        icon="pi pi-plus"
                                        className="p-button-info"
                                        onClick={this.addNewEntry}
                                        style={{ width: '100%', padding: '0', color: 'darkslategray' }}
                                    />
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
    }
}

export default StockEntryTable;